/**
 * PDF rendering configuration constants
 * These values control the dimensions and quality of the generated PDF
 */
export const PDF_RENDER_CONFIG = {
  VIEWPORT_WIDTH: 600,
  VIEWPORT_HEIGHT: 900,
  CANVAS_SCALE: 3,
  BACKGROUND_COLOR: '#0a1a2c',
  MAX_WAIT_MS: 5000,
  POLL_INTERVAL_MS: 50
} as const;

/**
 * Waits for the iframe content to be fully loaded and ready for rendering
 * Checks for image loading completion and DOM stability
 */
export const waitForContentReady = async (
  iframeDoc: Document,
  maxWaitMs: number = PDF_RENDER_CONFIG.MAX_WAIT_MS
): Promise<void> => {
  const startTime = Date.now();

  const checkReady = (): boolean => {
    // Check if all images are loaded
    const images = Array.from(iframeDoc.images);
    const allImagesLoaded =
      images.length === 0 || images.every((img) => img.complete && img.naturalHeight !== 0);

    // Check if document is in ready state
    const documentReady = iframeDoc.readyState === 'complete';

    return allImagesLoaded && documentReady;
  };

  // Wait for content window load event (with timeout fallback)
  await new Promise<void>((resolve) => {
    if (iframeDoc.readyState === 'complete') {
      resolve();
    } else if (iframeDoc.defaultView) {
      iframeDoc.defaultView.addEventListener('load', () => resolve(), { once: true });
      // Fallback timeout in case load event never fires
      setTimeout(resolve, 1000);
    } else {
      // If no defaultView, just resolve immediately
      resolve();
    }
  });

  // Poll for content readiness with timeout
  return new Promise((resolve, reject) => {
    const poll = () => {
      if (checkReady()) {
        resolve();
        return;
      }

      if (Date.now() - startTime > maxWaitMs) {
        reject(new Error('Timeout waiting for content to be ready'));
        return;
      }

      setTimeout(poll, PDF_RENDER_CONFIG.POLL_INTERVAL_MS);
    };

    poll();
  });
};

/**
 * Applies PDF-specific styling adjustments to the iframe document
 * Hides buttons and adjusts margins for optimal PDF rendering
 */
export const applyPdfStyling = (iframeDoc: Document): void => {
  const pdfButton = iframeDoc.getElementById('pdf-download-btn');
  if (pdfButton) {
    pdfButton.style.display = 'none';
  }

  const header = iframeDoc.querySelector('.header');
  if (header instanceof HTMLElement) {
    header.style.marginTop = '0px';
    header.style.marginBottom = '16px';
  }

  const container = iframeDoc.getElementById('qr-content-container');
  if (container instanceof HTMLElement) {
    container.style.paddingTop = '0px';
  }

  const body = iframeDoc.body;
  if (body) {
    body.style.margin = '0px';
    body.style.display = 'flex';
    body.style.alignItems = 'flex-start';
    body.style.justifyContent = 'center';
    body.style.paddingTop = '30px';
  }
};

/**
 * Creates a hidden iframe for PDF rendering
 * Returns the iframe element and a cleanup function
 */
export const createHiddenIframe = (): {
  iframe: HTMLIFrameElement;
  cleanup: () => void;
} => {
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.left = '-9999px';
  iframe.style.width = `${PDF_RENDER_CONFIG.VIEWPORT_WIDTH}px`;
  iframe.style.height = `${PDF_RENDER_CONFIG.VIEWPORT_HEIGHT}px`;
  document.body.appendChild(iframe);

  const cleanup = () => {
    if (iframe.parentNode) {
      document.body.removeChild(iframe);
    }
  };

  return { iframe, cleanup };
};
